export const PDP_DIETARY_CODES = [
  "dairy_free",
  "gluten_free",
  "sugar_free",
  "nut_free",
  "vegan",
  "vegetarian",
  "organic",
  "gluten",
  "nuts_sesame",
  "dairy",
  "egg",
  "seafood_shellfish",
  "chilli",
  "soy",
];

export const DIETARY_CODE_DEFINITIONS = {
  dairy_free: {
    en: "Dairy free",
    ar: "خالٍ من منتجات الألبان",
    kind: "dietary",
  },
  gluten_free: { en: "Gluten free", ar: "خالٍ من الغلوتين", kind: "dietary" },
  sugar_free: { en: "Sugar free", ar: "خالٍ من السكر", kind: "dietary" },
  nut_free: { en: "Nut free", ar: "خالٍ من المكسرات", kind: "dietary" },
  vegan: { en: "Vegan", ar: "نباتي صرف", kind: "dietary" },
  vegetarian: { en: "Vegetarian", ar: "نباتي", kind: "dietary" },
  organic: { en: "Organic", ar: "عضوي", kind: "dietary" },
  gluten: { en: "Gluten", ar: "غلوتين", kind: "allergen" },
  nuts_sesame: { en: "Nuts & sesame", ar: "مكسرات وسمسم", kind: "allergen" },
  dairy: { en: "Dairy", ar: "منتجات الألبان", kind: "allergen" },
  egg: { en: "Egg", ar: "بيض", kind: "allergen" },
  seafood_shellfish: {
    en: "Seafood & shellfish",
    ar: "مأكولات بحرية ومحاريات",
    kind: "allergen",
  },
  chilli: { en: "Chilli", ar: "فلفل حار", kind: "allergen" },
  soy: { en: "Soy", ar: "صويا", kind: "allergen" },
};

export const PDP_ALLERGEN_CODES = PDP_DIETARY_CODES.filter(
  (code) => DIETARY_CODE_DEFINITIONS[code].kind === "allergen",
);

const aliases = {
  "dairy free": "dairy_free",
  dairy_free: "dairy_free",
  "gluten free": "gluten_free",
  gluten_free: "gluten_free",
  "sugar free": "sugar_free",
  sugar_free: "sugar_free",
  "nut free": "nut_free",
  nut_free: "nut_free",
  vegan: "vegan",
  vegetarian: "vegetarian",
  organic: "organic",
  gluten: "gluten",
  "nuts/sesame": "nuts_sesame",
  "nuts & sesame": "nuts_sesame",
  nuts_sesame: "nuts_sesame",
  dairy: "dairy",
  milk: "dairy",
  egg: "egg",
  eggs: "egg",
  "seafood/shellfish": "seafood_shellfish",
  "seafood & shellfish": "seafood_shellfish",
  seafood_shellfish: "seafood_shellfish",
  chilli: "chilli",
  chili: "chilli",
  soy: "soy",
  soya: "soy",
  soybean: "soy",
};

const tokenize = (value) => {
  if (Array.isArray(value)) return value.flatMap(tokenize);
  if (value === null || value === undefined) return [];
  return String(value)
    .split(/[,;|]+/)
    .map((token) => token.trim())
    .filter(Boolean);
};

export const normalizeDietaryCodes = (value) => {
  const seen = new Set();
  return tokenize(value).reduce((codes, token) => {
    const lookup = token.toLowerCase().replace(/[-_]+/g, " ").trim();
    const code = aliases[lookup] || aliases[token.toLowerCase()];
    const preserved = code ||
      (token.startsWith("legacy:") ? token : `legacy:${token}`);
    const identity = preserved.toLowerCase();
    if (!seen.has(identity)) {
      seen.add(identity);
      codes.push(preserved);
    }
    return codes;
  }, []);
};

export const getDietaryCodeLabel = (code, language = "english") => {
  const definition = DIETARY_CODE_DEFINITIONS[code];
  if (definition) return language === "arabic" ? definition.ar : definition.en;
  return String(code).startsWith("legacy:") ? String(code).slice(7) : code;
};

export const splitDietaryCodes = (value) => {
  const codes = normalizeDietaryCodes(value);
  return {
    allergens: codes.filter(
      (code) => DIETARY_CODE_DEFINITIONS[code]?.kind === "allergen",
    ),
    dietary: codes.filter(
      (code) => DIETARY_CODE_DEFINITIONS[code]?.kind === "dietary",
    ),
    unknown: codes.filter((code) => !DIETARY_CODE_DEFINITIONS[code]),
  };
};

export const collectSelectedDietaryCodes = ({
  product = {},
  selectedOptions = {},
} = {}) => {
  const values = [
    ...tokenize(product.dietary_restriction_codes),
    ...tokenize(product.dietary_restrictions),
  ];
  const productOptions = Array.isArray(product.options) ? product.options : [];
  Object.entries(selectedOptions || {}).forEach(([optionKey, selection]) => {
    const optionId = String(optionKey).split("-")[0];
    const option = productOptions.find(
      ({ id }) => String(id) === String(optionId),
    );
    if (!option) return;
    const selectedChoiceIds = Object.keys(selection.selectedOptions || {});
    (Array.isArray(option.choices) ? option.choices : []).forEach((choice) => {
      if (selectedChoiceIds.includes(String(choice.id))) {
        values.push(
          ...tokenize(choice.dietary_restriction_codes),
          ...tokenize(choice.dietary_restrictions),
        );
      }
    });
  });
  return normalizeDietaryCodes(values);
};
